import { parseAbsolute } from '@internationalized/date';
import { OPERATION_AT_TX_CENTER_DURATION } from '@lp/features/RoutePlanner/config';
import { isPrimaryHospital, isPrimaryTxCenter } from '@lp/features/RoutePlanner/utils/case-locations';
import { AviaLegTypes, GroundLegTypes, LegLocationTypeEnum, SegmentTypeEnum } from '@/api/apis/route';
import { RouteSegmentType } from '@/types/route';
import { getDefaultDryRunSegments, getDefaultSegments, GetDefaultSegmentsParams } from '@/utils/route';

export const getDryRunRouteSegments = ({
  donorHospital,
  donorHospitalDateTimeString,
  legs,
  arrivalHub,
}: Pick<GetDefaultSegmentsParams, 'donorHospital' | 'donorHospitalDateTimeString' | 'arrivalHub'> & {
  legs: (AviaLegTypes | GroundLegTypes)[];
}): RouteSegmentType[] => {
  const defaultDryRunSegments = getDefaultDryRunSegments(donorHospital, donorHospitalDateTimeString, arrivalHub);

  if (!legs.length) {
    return defaultDryRunSegments;
  }

  const dryRunLeg = legs.find((a) => a.segmentType === SegmentTypeEnum.dhToHub);
  const dryRunStart = legs.find(
    (a) =>
      a.segmentType === SegmentTypeEnum.dhToHub &&
      isPrimaryHospital(a.departureLocation, { donorHospitalId: donorHospital.id }),
  );

  return [...defaultDryRunSegments].map((s) => {
    if (s.type === SegmentTypeEnum.dhToHub) {
      return {
        ...s,
        ...(dryRunLeg ? { id: dryRunLeg.chainPosition.groupId } : {}),
        ...(dryRunStart
          ? { actualStartTime: parseAbsolute(dryRunStart.departureTime, dryRunStart.departureLocalTimezone).toString() }
          : {}),
      };
    }

    return s;
  });
};

export const getRouteSegments = ({
  donorHospital,
  txCenter,
  legs,
  donorHospitalDateTimeString,
  recipientHospitalDateTimeString,
  donorOperationEndDateTimeString,
  recipientOperationEndDateTimeString,
  departureHub,
  arrivalHub,
}: GetDefaultSegmentsParams & { legs: (AviaLegTypes | GroundLegTypes)[] }): RouteSegmentType[] => {
  const defaultSegments = getDefaultSegments({
    donorHospital,
    txCenter,
    donorHospitalDateTimeString,
    recipientHospitalDateTimeString,
    donorOperationEndDateTimeString,
    recipientOperationEndDateTimeString,
    departureHub,
    arrivalHub,
  });

  if (!legs.length) {
    return defaultSegments;
  }

  const hubToDhLeg = legs.find((a) => a.segmentType === SegmentTypeEnum.hospital);
  const dhToTxcLeg = legs.find((a) => a.segmentType === SegmentTypeEnum.txcenter);
  const txcToHubLeg = legs.find((a) => a.segmentType === SegmentTypeEnum.hub);
  const toHospitalStartLeg = legs.find(
    (a) =>
      a.segmentType === SegmentTypeEnum.hospital &&
      a.departureLocation.locationType === LegLocationTypeEnum.hub &&
      a.departureLocation.locationId,
  );
  const primary = { donorHospitalId: donorHospital.id, txCenterId: txCenter?.id };
  const fromHospitalLeg = legs.find(
    (a) =>
      a.segmentType === SegmentTypeEnum.txcenter &&
      isPrimaryHospital(a.departureLocation, primary) &&
      a.departureLocation.locationId,
  );
  const toTxCenterLeg = legs.find(
    (a) =>
      a.segmentType === SegmentTypeEnum.txcenter &&
      isPrimaryTxCenter(a.arrivalLocation, primary) &&
      a.arrivalLocation.locationId,
  );
  const fromTxCenterLeg = legs.find(
    (a) =>
      a.segmentType === SegmentTypeEnum.hub &&
      isPrimaryTxCenter(a.departureLocation, primary) &&
      a.departureLocation.locationId,
  );

  return [...defaultSegments].map((s) => {
    switch (s.type) {
      case SegmentTypeEnum.hospital:
        let hospitalSegment = { ...s } as RouteSegmentType;

        if (hubToDhLeg) {
          hospitalSegment = {
            ...hospitalSegment,
            id: hubToDhLeg.chainPosition.groupId,
          };
        }

        if (toHospitalStartLeg) {
          hospitalSegment = {
            ...hospitalSegment,
            startTime: parseAbsolute(
              toHospitalStartLeg.departureTime,
              toHospitalStartLeg.departureLocalTimezone,
            ).toString(),
            actualStartTime: parseAbsolute(
              toHospitalStartLeg.departureTime,
              toHospitalStartLeg.departureLocalTimezone,
            ).toString(),
          };
        }

        return hospitalSegment;
      case SegmentTypeEnum.txcenter:
        let dhToTxSegment = { ...s } as RouteSegmentType;

        if (dhToTxcLeg) {
          dhToTxSegment = {
            ...dhToTxSegment,
            id: dhToTxcLeg.chainPosition.groupId,
          };
        }

        if (fromHospitalLeg) {
          dhToTxSegment = {
            ...dhToTxSegment,
            actualStartTime: parseAbsolute(
              fromHospitalLeg.departureTime,
              fromHospitalLeg.departureLocalTimezone,
            ).toString(),
          };
        }

        return dhToTxSegment;
      case SegmentTypeEnum.hub:
        let txcToHubSegment = { ...s } as RouteSegmentType;

        if (txcToHubLeg) {
          txcToHubSegment = {
            ...txcToHubSegment,
            id: txcToHubLeg.chainPosition.groupId,
          };
        }

        if (toTxCenterLeg) {
          txcToHubSegment = {
            ...txcToHubSegment,
            startTime: parseAbsolute(toTxCenterLeg.arrivalTime, toTxCenterLeg.arrivalLocalTimezone)
              .add({ milliseconds: OPERATION_AT_TX_CENTER_DURATION })
              .toString(),
          };
        }

        if (fromTxCenterLeg) {
          txcToHubSegment = {
            ...txcToHubSegment,
            actualStartTime: parseAbsolute(
              fromTxCenterLeg.departureTime,
              fromTxCenterLeg.departureLocalTimezone,
            ).toString(),
          };
        }

        return txcToHubSegment;
      default:
        return s;
    }
  });
};

export const getSegment = (segments: RouteSegmentType[], currentSegmentId: string) => {
  return segments.find((segment) => currentSegmentId === segment.id) || null;
};
